import { Badge, BadgeProps, Button, FormCheck, FormControl, FormGroup, FormLabel, Modal, Table } from "react-bootstrap"
import { WorkstationTimestamp } from "../types/WorkstationTimestamp"
import { Fragment, ReactElement, useContext } from "react"
import { Async, Operation, Variable } from "react-declarative-state"
import moment from "moment"
import _, { truncate } from "lodash"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEdit,
  faTrash,
  faSave,
  faCancel,
  faRecycle,
  faCode,
  faList,
  faHashtag,
  faWrench,
} from "@fortawesome/free-solid-svg-icons"
import { Workstation } from "../types/Workstation"
import { toast } from "../portals/toast"
import { editWorkstationTimeStamp, voidWorkstationTimeStamp } from "../services/workStationService"
import { SessionContext } from "../types/Session"
import { printQRCode } from "../utility/ZebraPrinterFunctions"

const IconWrapper: React.FC<{ children: ReactElement; bg?: BadgeProps["bg"]; size: number }> = ({
  children,
  bg,
  size,
}) => {
  return (
    <div
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
      className={`d-flex bg-${bg || "secondary"} rounded-circle justify-content-center align-items-center me-2`}
    >
      {children}
    </div>
  )
}

const coatingOptions = [
  { value: "Ni", label: "Ni - Nikkeli" },
  { value: "NS", label: "NS - Nikkeli - Tina" },
  { value: "Sn", label: "Sn - Tina" },
  { value: "SURTEC650", label: "SURTEC650 - SurTec 650" },
]

const TimestampsTable: React.FC<{
  timestamps: WorkstationTimestamp[]
  setTimestamps: (timestamps: WorkstationTimestamp[]) => void
  selectedWorkStation: Workstation
}> = ({ timestamps, selectedWorkStation, setTimestamps }) => {
  const context = useContext(SessionContext)
  return (
    <Table className="mb-5" responsive>
      <tbody>
        {_.orderBy(timestamps, (t) => t.createdAt, "desc").map(
          ({
            id,
            batchNumber,
            toolNumber,
            batchSize,
            createdAt,
            voidedAt,
            isReWork,
            customField0,
            customField1,
            program,
          }) => (
            <Fragment key={id}>
              <Variable
                initialValue={{
                  id,
                  batchNumber,
                  toolNumber,
                  batchSize,
                  isReWork,
                  createdAt,
                  editing: false,
                  customField0,
                  customField1,
                  program,
                }}
              >
                {(editable, setEditable) => (
                  <>
                    <tr>
                      <td className="small text-muted pb-0" style={{ borderBottom: "none" }} colSpan={5}>
                        <div className="d-inline" style={voidedAt ? { textDecoration: "line-through" } : {}}>
                          {moment(createdAt).format("llll")}
                        </div>
                        {!!voidedAt && (
                          <Badge className="ms-2" bg="secondary">
                            Mitätöity
                          </Badge>
                        )}
                      </td>
                    </tr>
                    <tr className={voidedAt ? "text-muted" : undefined}>
                      <td style={{ borderBottom: "none" }}>
                        <div className="d-flex align-items-center">
                          <IconWrapper size={20}>
                            <FontAwesomeIcon style={{ fontSize: 12, color: "white" }} icon={faHashtag} />
                          </IconWrapper>
                          {editable.editing ? (
                            <FormControl
                              value={editable.batchNumber}
                              onChange={(e) => setEditable({ ...editable, batchNumber: e.target.value })}
                            ></FormControl>
                          ) : (
                            truncate(batchNumber)
                          )}
                        </div>
                      </td>
                      <td style={{ borderBottom: "none" }}>
                        <div className="d-flex align-items-center">
                          <IconWrapper size={20}>
                            <FontAwesomeIcon style={{ fontSize: 12, color: "white" }} icon={faWrench} />
                          </IconWrapper>
                          {editable.editing ? (
                            <FormControl
                              value={editable.toolNumber}
                              onChange={(e) => setEditable({ ...editable, toolNumber: e.target.value })}
                            ></FormControl>
                          ) : (
                            truncate(toolNumber) || "-"
                          )}
                        </div>
                      </td>
                      <td style={{ borderBottom: "none" }}>
                        <div className="d-flex align-items-center">
                          <IconWrapper size={20}>
                            <FontAwesomeIcon icon={faList} fontSize={12} color="white" />
                          </IconWrapper>
                          {editable.editing ? (
                            <FormControl
                              value={editable.batchSize}
                              type="number"
                              onChange={(e) =>
                                setEditable({
                                  ...editable,
                                  batchSize: parseInt(e.target.value, 10),
                                })
                              }
                            ></FormControl>
                          ) : (
                            <>{batchSize} kpl</>
                          )}
                        </div>
                      </td>
                      <td style={{ borderBottom: "none" }}>
                        {editable.editing ? (
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => setEditable({ ...editable, isReWork: !editable.isReWork })}
                            className="p-2"
                          >
                            <FormCheck checked={editable.isReWork} label="Korjaustyö" />
                          </div>
                        ) : (
                          <>
                            {isReWork ? (
                              <Badge bg="warning">
                                <FontAwesomeIcon className="me-2" icon={faRecycle} />
                                korjaustyö
                              </Badge>
                            ) : (
                              ""
                            )}
                          </>
                        )}
                      </td>
                      <td
                        rowSpan={2}
                        style={{
                          verticalAlign: "middle",
                          textAlign: "right",
                          minWidth: 180,
                          width: 180,
                        }}
                      >
                        <Operation
                          onDone={(updated: WorkstationTimestamp) => {
                            toast("Merkintä päivitetty", "success")
                            setTimestamps(timestamps.map((t) => (t.id === updated.id ? updated : t)))
                          }}
                        >
                          {(doRowCorrectionOperation, progress) => (
                            <>
                              {editable.editing ? (
                                <>
                                  <Button
                                    disabled={progress === Async.Progress.Progressing}
                                    size="sm"
                                    className="me-2"
                                    variant="danger"
                                    title="Mitätöi rivi"
                                    onClick={() => {
                                      doRowCorrectionOperation(
                                        voidWorkstationTimeStamp(context, editable.id).do(
                                          () => {
                                            toast("Tapahtuma mitätöity", "success")
                                            setEditable({ ...editable, editing: false })
                                          },
                                          () => {
                                            toast("Tapahtuman mitätöinti epäonnistui", "danger")
                                          }
                                        )
                                      )
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    disabled={progress === Async.Progress.Progressing}
                                    className="me-2"
                                    onClick={() => {
                                      if (
                                        !_.isEqual(
                                          {
                                            batchNumber,
                                            toolNumber,
                                            batchSize,
                                            isReWork,
                                            customField0,
                                            customField1,
                                            program,
                                          },
                                          _.pick(editable, [
                                            "batchNumber",
                                            "toolNumber",
                                            "batchSize",
                                            "isReWork",
                                            "customField0",
                                            "customField1",
                                            "program",
                                          ])
                                        )
                                      ) {
                                        doRowCorrectionOperation(
                                          editWorkstationTimeStamp(context, editable.id, {
                                            batchNumber: editable.batchNumber,
                                            toolNumber: editable.toolNumber,
                                            batchSize: editable.batchSize,
                                            isReWork: editable.isReWork,
                                            customField0: editable.customField0,
                                            customField1: editable.customField1,
                                            program: editable.program,
                                          }).do(
                                            () => {
                                              setEditable({ ...editable, editing: false })
                                            },
                                            () => {
                                              toast("Muokkaus epäonnistui. Yritä myöhemmin uudelleen", "danger")
                                            }
                                          )
                                        )
                                      }
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faSave} />
                                  </Button>
                                  <Button
                                    disabled={progress === Async.Progress.Progressing}
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => setEditable({ ...editable, editing: false })}
                                  >
                                    <FontAwesomeIcon icon={faCancel} />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <div className="d-flex align-items-center">
                                    <Variable initialValue={false}>
                                      {(showModal, setShowModal) => (
                                        <>
                                          <Button
                                            className="me-2 text-nowrap"
                                            variant="secondary"
                                            onClick={() => setShowModal(true)}
                                          >
                                            Tulosta tarrat
                                          </Button>
                                          {showModal && (
                                            <Modal show={showModal} onHide={() => setShowModal(false)}>
                                              <Modal.Header closeButton>
                                                <Modal.Title>Tarrojen tulostus</Modal.Title>
                                              </Modal.Header>
                                              <Variable
                                                initialValue={{
                                                  workNumber: batchNumber,
                                                  coating: coatingOptions[0].value,
                                                  abbMaterialNumber: "",
                                                  revisionNumber: "A",
                                                  copies: batchSize,
                                                }}
                                              >
                                                {(data, setData) => (
                                                  <>
                                                    <Modal.Body>
                                                      <FormGroup className="mb-3">
                                                        <FormLabel>Työmääräimen numero</FormLabel>
                                                        <FormControl
                                                          type="text"
                                                          value={data.workNumber}
                                                          onChange={(e: any) =>
                                                            setData({ ...data, workNumber: e.target.value })
                                                          }
                                                        />
                                                      </FormGroup>
                                                      <FormGroup className="mb-3">
                                                        <FormLabel>Pinnoite</FormLabel>
                                                        <FormControl
                                                          as="select"
                                                          value={data.coating}
                                                          onChange={(e: any) =>
                                                            setData({ ...data, coating: e.target.value })
                                                          }
                                                        >
                                                          {coatingOptions.map((co) => (
                                                            <option key={co.value} value={co.value}>
                                                              {co.label}
                                                            </option>
                                                          ))}
                                                        </FormControl>
                                                      </FormGroup>
                                                      <FormGroup className="mb-3">
                                                        <FormLabel>ABB materiaalin numero</FormLabel>
                                                        <FormControl
                                                          type="text"
                                                          value={data.abbMaterialNumber}
                                                          onChange={(e: any) =>
                                                            setData({ ...data, abbMaterialNumber: e.target.value })
                                                          }
                                                        />
                                                        {![0, 8, 14, 15].includes(data.abbMaterialNumber.length) && (
                                                          <div className="text-danger">
                                                            Materiaalinumeron pituus oltava 8, 14 tai 15 merkkiä.
                                                          </div>
                                                        )}
                                                      </FormGroup>
                                                      <FormGroup className="mb-3">
                                                        <FormLabel>Revisionumero</FormLabel>
                                                        <FormControl
                                                          type="text"
                                                          autoFocus
                                                          value={data.revisionNumber}
                                                          onChange={(e: any) =>
                                                            setData({ ...data, revisionNumber: e.target.value })
                                                          }
                                                        />
                                                        {data.revisionNumber.length > 1 && (
                                                          <div className="text-danger">
                                                            Revisionumeron pituus oltava 1 merkki.
                                                          </div>
                                                        )}
                                                      </FormGroup>
                                                      <FormGroup className="mb-3">
                                                        <FormLabel>Tarrojen määrä</FormLabel>
                                                        <FormControl
                                                          type="number"
                                                          value={data.copies}
                                                          onChange={(e: any) =>
                                                            setData({ ...data, copies: e.target.value })
                                                          }
                                                        />
                                                      </FormGroup>
                                                    </Modal.Body>
                                                    <Modal.Footer>
                                                      <Button variant="light" onClick={() => setShowModal(false)}>
                                                        Peruuta
                                                      </Button>
                                                      <Button
                                                        disabled={
                                                          !data.copies ||
                                                          !data.revisionNumber ||
                                                          data.revisionNumber.length !== 1 ||
                                                          !data.abbMaterialNumber ||
                                                          ![8, 14, 15].includes(data.abbMaterialNumber.length) ||
                                                          !data.workNumber
                                                        }
                                                        variant="primary"
                                                        onClick={async () => {
                                                          const yearWeek = moment().format("YWW").slice(-3)
                                                          console.log({ yearWeek })
                                                          const serialNumber =
                                                            data.workNumber + moment().format("YYMMDDHHmm")
                                                          const mfgLocation = "SU"
                                                          const separator = "-"
                                                          const printData = [
                                                            data.abbMaterialNumber,
                                                            data.revisionNumber,
                                                            yearWeek,
                                                            serialNumber,
                                                            mfgLocation,
                                                            separator,
                                                            data.coating,
                                                          ].join("")

                                                          const confirmed =
                                                            data.copies < 40 ||
                                                            window.confirm(
                                                              `Haluatko varmasti tulostaa ${data.copies} kpl tarroja?`
                                                            )

                                                          console.log({ printData })
                                                          setShowModal(false)
                                                          if (confirmed) {
                                                            await printQRCode(printData, data.copies, printData)
                                                          }
                                                        }}
                                                      >
                                                        Tulosta
                                                      </Button>
                                                    </Modal.Footer>
                                                  </>
                                                )}
                                              </Variable>
                                            </Modal>
                                          )}
                                        </>
                                      )}
                                    </Variable>{" "}
                                    <Button
                                      disabled={!!voidedAt || progress === Async.Progress.Progressing}
                                      onClick={() => {
                                        setEditable({ ...editable, editing: true })
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </Operation>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {selectedWorkStation.usesPrograms && (
                          <div className="d-flex align-items-center">
                            <IconWrapper size={20}>
                              <FontAwesomeIcon icon={faCode} color="white" fontSize={10} />
                            </IconWrapper>
                            {editable.editing ? (
                              <FormControl
                                value={editable.program || ""}
                                type="text"
                                onChange={(e) =>
                                  setEditable({
                                    ...editable,
                                    program: e.target.value,
                                  })
                                }
                              ></FormControl>
                            ) : (
                              program
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {selectedWorkStation.customField0Name && (
                          <div className="d-flex align-items-center">
                            <IconWrapper size={20}>
                              <div style={{ fontSize: 12, color: "white", fontWeight: "bold" }}>1</div>
                            </IconWrapper>
                            {editable.editing ? (
                              <FormControl
                                value={editable.customField0 || ""}
                                type={selectedWorkStation.customField0Type || "text"}
                                onChange={(e) =>
                                  setEditable({
                                    ...editable,
                                    customField0: e.target.value,
                                  })
                                }
                              ></FormControl>
                            ) : (
                              customField0
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {selectedWorkStation.customField1Name && (
                          <div className="d-flex align-items-center">
                            <IconWrapper size={20}>
                              <div style={{ fontSize: 12, color: "white", fontWeight: "bold" }}>2</div>
                            </IconWrapper>
                            {editable.editing ? (
                              <FormControl
                                value={editable.customField1 || ""}
                                type={selectedWorkStation.customField1Type || "text"}
                                onChange={(e) =>
                                  setEditable({
                                    ...editable,
                                    customField1: e.target.value,
                                  })
                                }
                              ></FormControl>
                            ) : (
                              customField1
                            )}
                          </div>
                        )}
                      </td>
                      <td></td>
                    </tr>
                  </>
                )}
              </Variable>
            </Fragment>
          )
        )}
      </tbody>
    </Table>
  )
}

export default TimestampsTable
